import sortRowsByField from './sortRowsByField';

/**
 * Given an entity type (company or member), an entity ID, an array of
 * contributions objects, and a map of entity data indexed by entity ID, returns
 * a data structure that contains information about total contributions to the
 * provided entity with several breakdowns.
 *
 * @param {string} entityType - The type of entity to look up.
 * @param {string|number} id - The entity ID to look up in the map.
 * @param {array} contributions - An array of contributions objects.
 * @param {Map} entityMap - A Map that connects entity IDs to objects.
 */
export default function getContributionsForEntity(
  entityType,
  id,
  contributions,
  entityMap
) {
  const fromEntityType = 'member' === entityType ? 'company' : 'member';
  const entityContributions = {
    currentCycle: 0,
    cycles: [],
    firstCycle: 2008, // Always start with the 2007-2008 election cycle.
    rows: [],
    totals: {
      byCycle: {},
      currentCycle: 0,
      grandTotal: 0,
    },
  };

  // Get the current cycle by extracting the latest value from contributions.
  entityContributions.currentCycle = contributions.reduce((acc, item) =>
    (item.cycle > acc ? item.cycle : acc), 0);

  // Populate cycles for result rows.
  for (
    let i = entityContributions.firstCycle;
    i <= entityContributions.currentCycle;
    i += 2
  ) {
    entityContributions.cycles.push(i);
  }

  // Filter contributions to only include this entity.
  const filteredContributions = contributions.filter((item) =>
    id === item[`${entityType}Id`]);

  // Loop through filtered contributions and add data to rows and totals.
  const contributionsById = {};
  filteredContributions.forEach((contribution) => {
    const {
      amount,
      [`${fromEntityType}Id`]: entityId,
      cycle,
    } = contribution;

    // Update grand total always.
    entityContributions.totals.grandTotal += amount;

    // Update current cycle total if contribution is during the current cycle.
    if (entityContributions.currentCycle === cycle) {
      entityContributions.totals.currentCycle += amount;
    }

    // Create or update the total for this cycle.
    if (entityContributions.totals.byCycle[cycle]) {
      entityContributions.totals.byCycle[cycle] += amount;
    } else {
      entityContributions.totals.byCycle[cycle] = amount;
    }

    // Add the entry for this entity, if it can be dereferenced.
    const fromEntity = entityMap.get(entityId);
    if (fromEntity) {
      if (contributionsById[entityId]) {
        contributionsById[entityId][cycle] = amount;
        contributionsById[entityId].total += amount;
      } else {
        contributionsById[entityId] = {
          [cycle]: amount,
          [fromEntityType]: fromEntity,
          total: amount,
        };
      }
    }
  });

  // Flatten contributions by company and sort by current cycle descending.
  entityContributions.rows = sortRowsByField(
    Object.values(contributionsById),
    entityContributions.currentCycle,
    'DESC'
  );

  return entityContributions;
}
